import { HttpParams } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { FormGroup } from '@angular/forms';
import { combineLatest, of, timer } from 'rxjs';
import { catchError, map, switchMap } from 'rxjs/operators';
import { IAddPresentialSchedule }
  from '../../@academy/create/add-presential-instance/add-presential-instance.component';
import { RoomService, ServiceBookingService, ServiceService } from '../data';
import { Room, Service, ServiceBooking } from '../models';
import { ToastrService } from '../utils';

@Injectable()
export class CourseInstanceFormService {

  constructor(
    private serviceBookingService: ServiceBookingService,
    private roomService: RoomService,
    private serviceService: ServiceService,
    private toastr: ToastrService) {
  }

  /**
   * Async validator that checks if service limits would be exceeded by creating a course instance
   *
   * @param form Course instance form
   * @param schedulerList List of schedule actions as found in course instance form component
   */
  checkServiceLimits(form: FormGroup, schedulerList: IAddPresentialSchedule[]) {
    return () => {
      const canCheck = !schedulerList ? false : !!schedulerList[0].room;
      if (!canCheck) {
        return of(null);
      }

      const start: string = schedulerList[0].day;
      const end: string = schedulerList[schedulerList.length - 1].day;
      const capacity: number = form.get('capacity').value;

      return timer(500)
        .pipe(
          switchMap(() => {
            if (!!schedulerList[0].room) {
              throw new Error('no room in scheduler');
            }

            return this.roomService.get(schedulerList[0].room.resourceId);
          }),
          switchMap((room) => combineLatest([
              this.serviceService.getAll('', this.getServiceParams(room)),
              this.serviceBookingService.getAll(this.getServiceBookingsParams(start, end, room)),
            ])),
          map(([services, bookings]) => [services['hydra:member'], bookings['hydra:member']]),
          map(([services, bookings]) => this.limitsExceeded(services, bookings, capacity)),
          map((exceeded) => exceeded ? {checkServiceLimits: true} : null),
          catchError(() => of(null)),
        );
    };
  }

  /**
   * Returns params used to get service bookings in order to validate service bookings quanties
   *
   * @param startDate Course instance start date
   * @param endDate Course instance end date
   * @param room One room related to this course instance
   */
  private getServiceBookingsParams(startDate: string, endDate: string, room: Room): HttpParams {
    return new HttpParams()
      .append('date[after]', startDate)
      .append('date[before]', endDate)
      .append('service.building', room.buildingId)
      .append('pagination', 'false');
  }

  /**
   * Returns params used to get services in order to validate service bookings quanties
   *
   * @param room One room related to this course instance
   */
  private getServiceParams(room: Room): HttpParams {
    const buildingId: string = room.building.toString().split('/').pop();

    return new HttpParams()
      .append('isAddedOnSession', 'true')
      .append('building', buildingId);
  }

  /**
   * Checks if limits would be exceeded by creating this course instance
   *
   * @param services Services to check limits of
   * @param bookings Existing bookings related to services
   * @param capacity Capacity of the course instance to create
   */
  private limitsExceeded(services: Service[], bookings: ServiceBooking[], capacity: number): boolean {
    for (const service of services) {
      const relatedBookings = bookings.filter(booking => booking.service === service['@id']);
      let sum = 0;

      relatedBookings.forEach(booking => {
        sum += booking.quantity;
      });

      if (sum + capacity > service.quantityLimitAdmin) {
        this.toastr.showCreationErrorToast('CREATE.ADD_PRESENTIAL.EXCEEDSERVICELIMITS');
        return true;
      }
    }

    return false;
  }
}
